import { useCallback, useEffect, useState } from "react";
import { App, Card, Col, Descriptions, Row, Space, Statistic, Switch, Table, Tag, Typography } from "antd";
import { adminFetch, errorMessage } from "../../lib/adminApi";
import type { NotificationsDashboardResponse, RuntimeFlag } from "../../types/notifications";
import { DELIVERY_STATUS_COLORS, FLAG_DESCRIPTIONS, formatDateTime } from "../../types/notifications";
import type { DeliveryStatus } from "../../types/notifications";
import { NOTIFICATION_TYPE_LABELS } from "../../types/filterAdmin";

const { Title, Paragraph, Text } = Typography;

/** Flagi binarne przełączamy switchem; inne (np. `search_engine`) tylko pokazujemy. */
function isBoolean(value: string | undefined): boolean {
  return value === undefined || value === "true" || value === "false";
}

export const NotificationsDashboard = () => {
  const { notification, modal } = App.useApp();
  const [data, setData] = useState<NotificationsDashboardResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await adminFetch<NotificationsDashboardResponse>("notifications/dashboard"));
    } catch (error) {
      notification.error({ message: "Nie udało się pobrać stanu potoku", description: errorMessage(error) });
    } finally {
      setLoading(false);
    }
  }, [notification]);

  useEffect(() => {
    void load();
  }, [load]);

  const setFlag = useCallback(
    async (key: string, value: string) => {
      setSavingKey(key);
      try {
        await adminFetch(`notifications/flags/${key}`, { method: "PUT", body: JSON.stringify({ value }) });
        notification.success({ message: `Flaga ${key} = ${value}` });
        await load();
      } catch (error) {
        notification.error({ message: "Nie udało się zapisać flagi", description: errorMessage(error) });
      } finally {
        setSavingKey(null);
      }
    },
    [load, notification],
  );

  const confirmFlag = useCallback(
    (key: string, value: string) => {
      // Hamulec i dry-run zmieniają to, co dostają klienci — świadoma decyzja, nie misclick.
      modal.confirm({
        title: `Ustawić ${key} = ${value}?`,
        content: FLAG_DESCRIPTIONS[key] ?? "",
        okText: "Zapisz",
        cancelText: "Anuluj",
        onOk: () => setFlag(key, value),
      });
    },
    [modal, setFlag],
  );

  const counts = data?.counts;
  const flagValue = (key: string) => data?.flags.find((f: RuntimeFlag) => f.key === key)?.value;
  const flagRows = (data?.togglable_flags ?? []).map((key) => ({ key, value: flagValue(key) }));

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Card loading={loading && !data}>
        <Title level={4}>Potok powiadomień</Title>
        <Paragraph type="secondary">
          Okna raportów i maili, wpisy w aplikacji i wysyłka działają w Mercury (<Text code>notifications_worker</Text>).
          Liczniki z Mongo <Text code>BestBiznesNotifications</Text>; ustawienia odbiorców są w Supabase.
        </Paragraph>
        {counts && (
          <Row gutter={[16, 16]}>
            <Col xs={12} md={6}>
              <Statistic title="Raporty 24 h" value={counts.reports_last_24h} />
            </Col>
            <Col xs={12} md={6}>
              <Statistic title="Wysyłki 24 h" value={counts.deliveries_last_24h} />
            </Col>
            <Col xs={12} md={6}>
              <Statistic
                title="Nieudane 24 h"
                value={counts.deliveries_failed_24h}
                valueStyle={counts.deliveries_failed_24h > 0 ? { color: "#cf1322" } : undefined}
              />
            </Col>
            <Col xs={12} md={6}>
              <Statistic title="Notatki czekające na mail" value={counts.pending_note_mail} />
            </Col>
          </Row>
        )}
      </Card>

      {counts && (
        <Card title="Szczegóły">
          <Descriptions column={{ xs: 1, md: 2 }} size="small" bordered>
            <Descriptions.Item label="Wysyłki wg statusu">
              <Space wrap>
                {Object.entries(counts.deliveries_by_status).map(([status, count]) => (
                  <Tag key={status} color={DELIVERY_STATUS_COLORS[status as DeliveryStatus] ?? "default"}>
                    {status}: {count}
                  </Tag>
                ))}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Ostatnia wysyłka">{formatDateTime(counts.last_delivery_at)}</Descriptions.Item>
            <Descriptions.Item label="Wpisy w aplikacji 24 h">
              <Space wrap>
                {Object.entries(counts.in_app_last_24h_by_type).map(([type, count]) => (
                  <Tag key={type}>
                    {NOTIFICATION_TYPE_LABELS[type] ?? type}: {count}
                  </Tag>
                ))}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Outbox (niewysłane komunikaty)">
              {counts.outbox > 0 ? <Text type="warning">{counts.outbox}</Text> : counts.outbox}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      )}

      <Card title="Flagi runtime">
        <Paragraph type="secondary">Worker i wyszukiwarka czytają je w każdym przebiegu — zmiana działa bez redeployu.</Paragraph>
        <Table
          rowKey="key"
          dataSource={flagRows}
          pagination={false}
          columns={[
            { title: "Flaga", dataIndex: "key", key: "key", render: (key: string) => <Text code>{key}</Text> },
            {
              title: "Wartość",
              key: "value",
              render: (_: unknown, row: { key: string; value?: string }) =>
                isBoolean(row.value) ? (
                  <Switch
                    checked={row.value === "true"}
                    loading={savingKey === row.key}
                    onChange={(checked) => confirmFlag(row.key, checked ? "true" : "false")}
                  />
                ) : (
                  <Text>{row.value}</Text>
                ),
            },
            {
              title: "Opis",
              key: "description",
              render: (_: unknown, row: { key: string; value?: string }) => (
                <Text type="secondary">
                  {FLAG_DESCRIPTIONS[row.key] ?? ""}
                  {row.value === undefined ? " (nieustawiona — obowiązuje wartość domyślna)" : ""}
                </Text>
              ),
            },
          ]}
        />
      </Card>
    </Space>
  );
};
